import { useState, useRef } from "react";
import MainDepartments from "./MainDepartments";
import SubDepartments from "./SubDepartments";
import InnerDepartments from "./InnerDepartments";
import { useMediaQuery } from "@mui/material";
import { TextField, InputAdornment } from "@mui/material";
import GreenButton from "../../../components/GreenButton/GreenButton";
import BlueButton from "../../../components/BlueButton/BlueButton";
import SearchIcon from "@mui/icons-material/Search";
import AddGif from "./../../../assets/add-gif.gif";
import MessageModals from "../../../components/MessageModals/MessageModals";
import SwipeableModals from "../SwipeableModals/SwipeableModals";
import usePermissions from "../../../hooks/usePermissions";

const FactoriesView = () => {
  const { hasPermission } = usePermissions();
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");
  const [departmentView, setDepartmentView] = useState("main");
  const [query, setQuery] = useState("");
  const [isSwipeableOpen, setIsSwipeableOpen] = useState(false);
  const [swipeableView, setSwipeableView] = useState("");
  const [isMessageOpen, setIsMessageOpen] = useState(false);
  const [messageView, setMessageView] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const handleOpenSwipeable = (view, department = null) => {
    setSwipeableView(view);
    setSelectedDepartment(department);
    setIsSwipeableOpen(true);
  };

  const handleOpenMessage = (view, department = null) => {
    setMessageView(view);
    setSelectedDepartment(department);
    setIsMessageOpen(true);
  };

  // Handle refreshing data in all components
  const refreshMainData = () => {
    if (
      mainDepartmentsRef.current &&
      typeof mainDepartmentsRef.current.getData === "function"
    ) {
      mainDepartmentsRef.current.getData();
    }
  };

  const refreshSubData = () => {
    if (
      subDepartmentsRef.current &&
      typeof subDepartmentsRef.current.getData === "function"
    ) {
      subDepartmentsRef.current.getData();
    }
  };

  const refreshInnerData = () => {
    if (
      innerDepartmentsRef.current &&
      typeof innerDepartmentsRef.current.getData === "function"
    ) {
      innerDepartmentsRef.current.getData();
    }
  };

  // References to department components to call methods directly
  const mainDepartmentsRef = useRef();
  const subDepartmentsRef = useRef();
  const innerDepartmentsRef = useRef();

  // Get the appropriate button text based on current view
  const getAddButtonText = () => {
    switch (departmentView) {
      case "main":
        return "اضافة قسم جديد";
      case "secondary":
        return "اضافة قسم فرعي";
      case "inner":
        return "اضافة قسم داخلي";
      default:
        return "اضافة قسم";
    }
  };

  // Get the appropriate swipeable view based on current department view
  const getSwipeableView = () => {
    switch (departmentView) {
      case "main":
        return "createDepartment";
      case "secondary":
        return "createSubDepartment";
      case "inner":
        return "createInnerDepartment";
      default:
        return "createDepartment";
    }
  };

  // Get the appropriate refresh function based on current view
  const getRefreshFunction = () => {
    switch (departmentView) {
      case "main":
        return refreshMainData;
      case "secondary":
        return refreshSubData;
      case "inner":
        return refreshInnerData;
      default:
        return refreshMainData;
    }
  };

  // Get the appropriate success message text
  const getSuccessMessage = () => {
    switch (departmentView) {
      case "main":
        return "تم حذف القسم بنجاح";
      case "secondary":
        return "تم حذف القسم الفرعي بنجاح";
      case "inner":
        return "تم حذف القسم الداخلي بنجاح";
      default:
        return "تم الحذف بنجاح";
    }
  };

  return (
    <div className="departments-container">
      <div className="actions-container">
        <div className="btns-container">
          <GreenButton
            text="الاقسام الرئيسية"
            size={isMobile ? "full" : isTablet ? "fit" : "medium"}
            variant={departmentView === "main" ? "fill" : "outlined"}
            onClick={() => setDepartmentView("main")}
          />
          <GreenButton
            text="الاقسام الفرعية"
            size={isMobile ? "full" : isTablet ? "fit" : "medium"}
            variant={departmentView === "secondary" ? "fill" : "outlined"}
            onClick={() => setDepartmentView("secondary")}
          />
          <GreenButton
            text="الاقسام الداخلية"
            size={isMobile ? "full" : isTablet ? "fit" : "medium"}
            variant={departmentView === "inner" ? "fill" : "outlined"}
            onClick={() => setDepartmentView("inner")}
          />
        </div>
        {hasPermission(94) && (
          <BlueButton
            text={getAddButtonText()}
            size={isMobile ? "full" : isTablet ? "fit" : "large"}
            variant="fill"
            onClick={() => handleOpenSwipeable(getSwipeableView())}
            icon={<img src={AddGif} alt="" />}
          />
        )}
      </div>

      <div className="search">
        <TextField
          id="outlined-search"
          type="search"
          value={query}
          placeholder="ابحث باسم القسم...."
          onChange={(e) => setQuery(e.target.value)}
          sx={{
            width: isMobile ? "100%" : isTablet ? "300px" : "360px",
            borderRadius: "8px",
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </div>

      {departmentView === "main" ? (
        <MainDepartments
          ref={mainDepartmentsRef}
          query={query}
          onEdit={(department) =>
            handleOpenSwipeable("editDepartment", department)
          }
          onDelete={(department) => handleOpenMessage("delete", department)}
        />
      ) : departmentView === "secondary" ? (
        <SubDepartments
          ref={subDepartmentsRef}
          query={query}
          onEdit={(department) =>
            handleOpenSwipeable("editSubDepartment", department)
          }
          onDelete={(department) => handleOpenMessage("delete", department)}
        />
      ) : (
        <InnerDepartments
          ref={innerDepartmentsRef}
          query={query}
          onEdit={(department) =>
            handleOpenSwipeable("editInnerDepartment", department)
          }
          onDelete={(department) => handleOpenMessage("delete", department)}
        />
      )}

      <SwipeableModals
        open={isSwipeableOpen}
        onClose={() => {
          setIsSwipeableOpen(false);
          setSelectedDepartment(null);
        }}
        onOpen={() => setIsSwipeableOpen(true)}
        currentView={swipeableView}
        type="factory"
        initialData={selectedDepartment}
        refreshData={getRefreshFunction()}
        isSubDepartment={swipeableView.includes("Sub")}
        isInnerDepartment={swipeableView.includes("Inner")}
      />

      <MessageModals
        open={isMessageOpen}
        onClose={() => {
          setIsMessageOpen(false);
          setSelectedDepartment(null);
        }}
        currentView={messageView}
        route={`/categories/${selectedDepartment?.id}`}
        text={getSuccessMessage()}
        onDataChange={getRefreshFunction()}
      />
    </div>
  );
};

export default FactoriesView;
